/**
 * Root-Cause Analysis - Step 1: Object Type Selection
 *
 * Select which object types to analyze (one or many)
 */

import React, { useState, useEffect } from 'react';
import { getOCELMetadata } from '../../../services/api/ocel.api';

const Step1_ObjectTypeSelection = ({ activeFile, initialSelectedObjectTypes = [], onNext }) => {
  const [objectTypes, setObjectTypes] = useState([]);
  const [selectedObjectTypes, setSelectedObjectTypes] = useState(() => new Set(initialSelectedObjectTypes));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch object types on mount
  useEffect(() => {
    fetchObjectTypes();
  }, [activeFile]);

  const fetchObjectTypes = async () => {
    if (!activeFile) return;
    setLoading(true);
    setError(null);

    try {
      const metadata = await getOCELMetadata(activeFile);

      console.log('OCEL Metadata:', metadata);

      if (metadata && metadata.object_types) {
        const objectTypesList = Object.keys(metadata.object_types);
        console.log('✅ Object types:', objectTypesList);
        setObjectTypes(objectTypesList);
      } else {
        setError('No object types found in OCEL metadata');
      }
    } catch (err) {
      console.error('Error fetching object types:', err);
      setError(err.message || 'Failed to load object types');
    } finally {
      setLoading(false);
    }
  };

  const toggleObjectType = (objType) => {
    setSelectedObjectTypes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(objType)) {
        newSet.delete(objType);
      } else {
        newSet.add(objType);
      }
      return newSet;
    });
  };

  const selectAllObjectTypes = () => {
    setSelectedObjectTypes(new Set(objectTypes));
  };

  const deselectAllObjectTypes = () => {
    setSelectedObjectTypes(new Set());
  };

  const handleNext = () => {
    if (selectedObjectTypes.size > 0) {
      onNext({
        selectedObjectTypes: Array.from(selectedObjectTypes)
      });
    }
  };

  // Render error state
  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800 font-medium">Error</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={fetchObjectTypes}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">Loading object types...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-6">
      {/* Header */}
      <div className="mb-4 flex-shrink-0">
        <h2 className="text-xl font-semibold text-gray-800">Step 1: Select Object Types</h2>
        <p className="text-sm text-gray-600 mt-1">
          Choose which object types to analyze. Selected: <span className="font-medium">{selectedObjectTypes.size}</span>
        </p>
      </div>

      {/* Object Type List with dynamic sizing */}
      <div className="flex-1 overflow-auto border border-gray-300 rounded-lg p-4 mb-4">
        {objectTypes.length > 0 ? (
          <div className="space-y-2">
            {/* Select All / Deselect All */}
            <div className="mb-4 pb-2 border-b border-gray-200">
              <button
                onClick={selectAllObjectTypes}
                className="text-sm text-blue-600 hover:underline mr-4"
              >
                Select All
              </button>
              <button
                onClick={deselectAllObjectTypes}
                className="text-sm text-red-600 hover:underline"
              >
                Clear Selection
              </button>
            </div>

            {objectTypes.map((objType) => {
              const isSelected = selectedObjectTypes.has(objType);
              return (
                <div
                  key={objType}
                  onClick={() => toggleObjectType(objType)}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleObjectType(objType)}
                      onClick={(e) => e.stopPropagation()}
                      className="mr-3 cursor-pointer"
                    />
                    <span className="text-lg font-medium text-gray-800">{objType}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            No object types found in this OCEL file.
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="flex justify-end items-center flex-shrink-0">
        <button
          onClick={handleNext}
          disabled={selectedObjectTypes.size === 0}
          className={`px-6 py-2 rounded font-medium ${
            selectedObjectTypes.size > 0
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

export default Step1_ObjectTypeSelection;
